"use client";
import { Input, useDisclosure, useToast } from "@chakra-ui/react";
import { useContext, useEffect, useState } from "react";
import HistorySection from "@/components/dashboard/history/HistorySection";
import HistoryRecordsTable from "@/components/dashboard/HistoryRecordsTable";
import { GetEmergencyContact } from "@/actions/templates/EmergencyContact/GetEmergencyContact";
import {
  EVENT_LIBRARY,
  HISTORY_TEMPLATES,
  PhoneNumberMask,
  REQUIRED_FIELD,
} from "@/lib/constants";
import {
  EmergencyContactPayload,
  PostEmergencyContact,
} from "@/actions/templates/EmergencyContact/PostEmergencyContact";
import { Controller, useForm } from "react-hook-form";
import { PutEmergencyContact } from "@/actions/templates/EmergencyContact/PutEmergencyContact";
import { DeleteEmergencyContact } from "@/actions/templates/EmergencyContact/DeleteEmergencyContact";
import HistoryModal from "@/components/dashboard/history/HistoryModal";
import FormRow from "@/components/dashboard/application/FormRow";
import GoogleAddressInput from "@/components/dashboard/GoogleAddressInput";
import LocationSelect from "@/components/LocationSelect";
import { AuthContext } from "@/providers/auth";
import PhoneNumberInput from "@/components/forms/PhoneNumberInput";
import ZipCodeInput from "@/components/forms/ZipCodeInput";
import useAnalytics from "@/lib/hooks/useAnalytics";
import { format } from "@react-input/mask";
import { formatPhoneNumber } from "@/utils/helpers";
import handleServerAction from "@/lib/hooks/handleServerAction";
import Select from "react-select";
import { ReactSelectOption } from "@/lib/types";
import { useIsTextMasked } from "../children/useIsDataMasked";
import { InputWrapper } from "@/components/forms/JailTextInputWrapper";

const category = "Contacts";
const defaultValues = HISTORY_TEMPLATES[category].defaultValues;

const RELATIONSHIP_OPTIONS: string[] = [
  "Family Member",
  "Attorney Representative",
  "Non-Attorney Representative",
  "Government Agency",
  "Non-Profit Organization/Legal Aid Group",
  "Health Service Agency/Hospital",
  "Nursing care Facility",
  "Friend",
  "Other",
];

const EmergencyHistoryPageContent = ({
  isJailUser,
}: {
  isJailUser?: boolean;
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const { getUserDetails } = useContext(AuthContext);
  const toast = useToast();
  const track = useAnalytics();

  const [isLoading, setIsLoading] = useState(true);
  const [records, setRecords] = useState<any[]>([]);
  const {
    handleSubmit,
    register,
    reset,
    control,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<EmergencyContactPayload>({
    defaultValues: defaultValues,
  });

  const getRecords = async () => {
    setIsLoading(true);
    const data = await handleServerAction(GetEmergencyContact);
    setRecords(data);
    setIsLoading(false);
  };

  useEffect(() => {
    getRecords();
  }, []);

  const onEdit = (id: number) => {
    const editRecord = records.find((record) => record.id === id);
    if (editRecord) {
      reset({
        Id: editRecord?.id,
        FirstName: editRecord?.firstName,
        LastName: editRecord?.lastName,
        Email: editRecord?.email,
        PhoneNumber: format(editRecord?.phoneNumber || "", PhoneNumberMask),
        StreetAddress: editRecord?.streetAddress,
        City: editRecord?.city,
        State: editRecord?.state,
        Zip: editRecord?.zip,
        Relationship: editRecord?.relationship,
      });
      onOpen();
    }
  };

  const refreshState = async () => {
    onClose();
    await getRecords();
    await getUserDetails();
    reset(defaultValues);
  };

  const onAdd = async (payload: EmergencyContactPayload) => {
    try {
      await handleServerAction(PostEmergencyContact, payload);
      await refreshState();
      toast({
        status: "success",
        title: "Record Added",
        description: "Record has been successfully added.",
      });

      track(EVENT_LIBRARY.ADDED_HISTORY, {
        category,
      });
    } catch (err) {
      toast({
        status: "error",
        title: "Error Occured",
        description: err.message,
      });
    }
  };

  const onUpdate = async (payload: EmergencyContactPayload) => {
    try {
      await handleServerAction(PutEmergencyContact, payload);
      await refreshState();
      toast({
        status: "success",
        title: "Record Updated",
        description: "Record has been successfully updated.",
      });

      track(EVENT_LIBRARY.UPDATED_HISTORY, {
        category,
      });
    } catch (err) {
      toast({
        status: "error",
        title: "Error Occured",
        description: err.message,
      });
    }
  };

  const onClick = () => {
    reset(defaultValues);
    onOpen();
  };

  const Id = watch("Id");
  const isTextMasked = useIsTextMasked() && Id;

  const optionalLabel = isJailUser ? "(Optional)" : "";
  return (
    <HistorySection category={category} isLoading={isLoading} onClick={onClick}>
      <HistoryRecordsTable
        category={category}
        records={records}
        cells={records.map((record) => [
          record.firstName,
          record.lastName,
          formatPhoneNumber(record.phoneNumber),
        ])}
        onEdit={onEdit}
        deleteAction={DeleteEmergencyContact}
        getRecords={getRecords}
      />
      <HistoryModal
        category={category}
        mode={Id ? "Update" : "Add"}
        isOpen={isOpen}
        onClose={onClose}
        onAdd={handleSubmit(onAdd)}
        onUpdate={handleSubmit(onUpdate)}
        isSubmitting={isSubmitting}
      >
        <FormRow label="First Name" size="sm" fieldError={errors.FirstName}>
          <InputWrapper
            name="FirstName"
            control={control}
            rules={{ required: REQUIRED_FIELD }}
            placeholder=""
            maskInputField={isTextMasked}
          />
        </FormRow>
        <FormRow label="Last Name" size="sm" fieldError={errors.LastName}>
          <InputWrapper
            name="LastName"
            control={control}
            rules={{ required: REQUIRED_FIELD }}
            placeholder=""
            maskInputField={isTextMasked}
          />
        </FormRow>

        <FormRow
          label={`Email ${optionalLabel}`}
          size="sm"
          fieldError={errors.Email}
        >
          <InputWrapper
            name="Email"
            control={control}
            rules={{ required: isJailUser ? undefined : REQUIRED_FIELD }}
            placeholder=""
            maskInputField={isTextMasked}
          />
        </FormRow>
        <FormRow
          label={`Street Address ${optionalLabel}`}
          size="sm"
          fieldError={errors.StreetAddress}
        >
          <Controller
            name="StreetAddress"
            control={control}
            rules={isJailUser ? undefined : { required: REQUIRED_FIELD }}
            render={({ field }) => (
              <GoogleAddressInput
                value={field.value}
                onChange={field.onChange}
                setValue={setValue}
                prefillFields={["City", "State", "Zip"]}
              />
            )}
          />
        </FormRow>
        <FormRow
          label={`City ${optionalLabel}`}
          size="sm"
          fieldError={errors.City}
        >
          <InputWrapper
            name="City"
            control={control}
            rules={{ required: isJailUser ? undefined : REQUIRED_FIELD }}
            placeholder=""
            maskInputField={isTextMasked}
          />
        </FormRow>
        <FormRow
          label={`State ${optionalLabel}`}
          size="sm"
          fieldError={errors.State}
        >
          <Controller
            name="State"
            control={control}
            rules={isJailUser ? undefined : { required: REQUIRED_FIELD }}
            render={({ field }) => (
              <LocationSelect
                valueKey="name"
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </FormRow>
        <FormRow
          label={`Zip Code ${optionalLabel}`}
          size="sm"
          fieldError={errors.Zip}
        >
          <ZipCodeInput isOptional register={register} />
        </FormRow>
        <FormRow label="Phone Number" size="sm" fieldError={errors.PhoneNumber}>
          <PhoneNumberInput register={register} fieldName="PhoneNumber" />
        </FormRow>
        <FormRow
          label="Relationship"
          size="sm"
          fieldError={errors.Relationship}
        >
          <Controller
            name="Relationship"
            control={control}
            rules={{ required: REQUIRED_FIELD }}
            render={({ field }) => (
              <Select
                {...field}
                placeholder="Select One"
                value={
                  field.value
                    ? {
                        label: field.value,
                        value: field.value,
                      }
                    : undefined
                }
                onChange={(selectedOption) =>
                  field.onChange(selectedOption?.value || "")
                }
                styles={{
                  container: (styles) => ({
                    ...styles,
                    width: "100%",
                  }),
                }}
                options={RELATIONSHIP_OPTIONS.map((opt) => ({
                  value: opt,
                  label: opt,
                }))}
              />
            )}
          />
        </FormRow>
      </HistoryModal>
    </HistorySection>
  );
};

export default EmergencyHistoryPageContent;
